import { useState, useEffect, useCallback, useRef } from 'react';
import '@/App.css';
import {
  SignIn,
  SignUp,
  UserButton,
  SignedIn,
  SignedOut,
  useUser,
  useAuth,
  useClerk,
  ClerkProvider,
} from '@clerk/clerk-react';
import { ThemeProvider, useTheme } from '@/components/ThemeProvider';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { LedgerTable } from '@/components/LedgerTable';
import { BalanceChart } from '@/components/BalanceChart';
import { TransactionForm } from '@/components/TransactionForm';
import { TransactionList } from '@/components/TransactionList';
import { SettingsPanel } from '@/components/SettingsPanel';
import { WorkspacePanel } from '@/components/WorkspacePanel';
import { JoinWorkspace } from '@/components/JoinWorkspace';
import { CSVImportWizard } from '@/components/CSVImportWizard';
import { PrivacyPolicy } from '@/components/PrivacyPolicy';
import * as analytics from '@/lib/analytics';
import { ContactDialog } from '@/components/ContactDialog';
import { TermsOfService } from '@/components/TermsOfService';
import LandingPage from '@/components/LandingPage';
import LearnMorePage from '@/components/LearnMorePage';
import UpgradeModal from '@/components/UpgradeModal';
import CheckoutSuccessPage from '@/components/CheckoutSuccessPage';
import CheckoutCancelPage from '@/components/CheckoutCancelPage';
import CheckoutResumePage from '@/components/CheckoutResumePage';
import CurrencySelect from '@/components/CurrencySelect';
import { formatCurrency as fmtCurrency, useCurrency } from '@/lib/currency';
import { useDocumentMeta } from '@/lib/useDocumentMeta';
import {
  getTransactions,
  createTransaction,
  updateTransaction,
  deleteTransaction,
  pauseTransaction,
  resumeTransaction,
  getLedger,
  setTokenGetter,
  migrateGuestData,
  getWorkspace,
  getMySubscription,
  createBillingPortal,
} from '@/lib/api';
import {
  getGuestTransactions,
  createGuestTransaction,
  updateGuestTransaction,
  deleteGuestTransaction,
  pauseGuestTransaction,
  resumeGuestTransaction,
  getGuestLedger,
  getGuestSettings,
  updateGuestSettings,
  hasGuestData,
  getGuestDataForMigration,
  clearGuestData,
  isBannerDismissed,
  dismissBanner,
} from '@/lib/guestStorage';
import {
  Plus,
  Settings,
  List,
  Sun,
  Moon,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  TableIcon,
  LineChart,
  X,
  LogIn,
  Sparkles,
  Users,
  Upload,
  Info,
  CreditCard,
  MoreVertical,
  Pencil,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const horizonOptions = [
  { value: '30', label: '30 Days' },
  { value: '90', label: '90 Days' },
  { value: '180', label: '6 Months' },
  { value: '365', label: '1 Year' },
  { value: '730', label: '2 Years' },
];

function computeStats(entries, minAlert, maxAlert) {
  if (!entries || entries.length === 0) return null;
  
  let minBal = entries[0].balance;
  let maxBal = entries[0].balance;
  let lowCount = 0;
  let highCount = 0;
  
  for (let i = 0; i < entries.length; i++) {
    const bal = entries[i].balance;
    if (bal < minBal) minBal = bal;
    if (bal > maxBal) maxBal = bal;
    if (entries[i].below_min_alert) lowCount++;
    if (entries[i].above_max_alert) highCount++;
  }
  
  return { minBalance: minBal, maxBalance: maxBal, lowAlertCount: lowCount, highAlertCount: highCount };
}

function Dashboard() {
  const { theme, toggleTheme } = useTheme();
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  const [currencyCode] = useCurrency();
  const formatCurrency = (v) => fmtCurrency(v, currencyCode);
  const [ledger, setLedger] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [horizon, setHorizon] = useState('90');
  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);
  const [workspace, setWorkspace] = useState(null);

  useDocumentMeta({ title: 'Dashboard — Cash Forth' });

  const [showTransactionForm, setShowTransactionForm] = useState(false);
  const [showTransactionList, setShowTransactionList] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showWorkspace, setShowWorkspace] = useState(false);
  const [showCSVImport, setShowCSVImport] = useState(false);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [showTermsOfService, setShowTermsOfService] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'chart'

  const handleManageSubscription = async () => {
    try {
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      const { data } = await createBillingPortal(origin);
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      toast.error('Could not open the billing portal. Please try again.');
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to open billing portal';
      toast.error(detail);
    }
  };

  // Set up auth token getter for API interceptor
  useEffect(function() {
    if (isLoaded && isSignedIn) {
      // Register the getToken function with the API module
      setTokenGetter(getToken);
      setAuthReady(true);
      
      // Identify user for analytics
      if (user) {
        analytics.identifyUser(user.id, {
          email: user.primaryEmailAddress?.emailAddress,
          signupMethod: user.externalAccounts?.[0]?.provider || 'email',
          accountAgeDays: user.createdAt ? Math.floor((Date.now() - new Date(user.createdAt).getTime()) / (1000 * 60 * 60 * 24)) : 0,
        });

        // One-time signup tracking for newly created accounts
        const ageMs = user.createdAt ? Date.now() - new Date(user.createdAt).getTime() : Infinity;
        if (ageMs < 2 * 60 * 1000) {
          analytics.trackSignupCompleted(user.id, {
            email: user.primaryEmailAddress?.emailAddress,
            signupMethod: user.externalAccounts?.[0]?.provider || 'email',
            createdAt: user.createdAt ? new Date(user.createdAt).toISOString() : new Date().toISOString(),
          });
        }
      }
    }
  }, [isLoaded, isSignedIn, getToken, user]);

  const loadData = useCallback(async () => {
    if (!authReady) return; // Don't load until auth is ready
    
    setLoading(true);
    try {
      const horizonDays = parseInt(horizon, 10);
      const [ledgerRes, txRes, workspaceRes] = await Promise.all([
        getLedger(horizonDays),
        getTransactions(),
        getWorkspace()
      ]);
      setLedger(ledgerRes.data);
      setTransactions(txRes.data);
      setWorkspace(workspaceRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [horizon, authReady]);

  // Load data when auth is ready or horizon changes
  useEffect(() => {
    if (authReady) {
      loadData();
    }
  }, [authReady, loadData]);

  const handleCreateTransaction = async (data) => {
    try {
      await createTransaction(data);
      toast.success('Transaction added');
      analytics.trackTransactionCreated(data);
      analytics.trackForecastUpdated(transactions.length + 1, 'manual_transaction');
      loadData();
    } catch (err) {
      console.error('Failed to create:', err);
      toast.error('Failed to add transaction');
    }
  };

  const handleUpdateTransaction = async (data) => {
    if (!editingTransaction) return;
    try {
      await updateTransaction(editingTransaction.id, data);
      toast.success('Transaction updated');
      analytics.trackTransactionEdited(data, Object.keys(data));
      setEditingTransaction(null);
      loadData();
    } catch (err) {
      console.error('Failed to update:', err);
      toast.error('Failed to update transaction');
    }
  };

  const handleDeleteTransaction = async (id) => {
    try {
      await deleteTransaction(id);
      toast.success('Transaction deleted');
      loadData();
    } catch (err) {
      console.error('Failed to delete:', err);
      toast.error('Failed to delete transaction');
    }
  };

  const handlePauseTransaction = async (id) => {
    try {
      await pauseTransaction(id);
      toast.success('Transaction paused');
      loadData();
    } catch (err) {
      console.error('Failed to pause:', err);
      toast.error('Failed to pause transaction');
    }
  };

  const handleResumeTransaction = async (id) => {
    try {
      await resumeTransaction(id);
      toast.success('Transaction resumed');
      loadData();
    } catch (err) {
      console.error('Failed to resume:', err);
      toast.error('Failed to resume transaction');
    }
  };

  const openEditForm = (tx) => {
    setEditingTransaction(tx);
    setShowTransactionList(false);
    setShowTransactionForm(true);
  };

  const entries = ledger?.entries || [];
  const stats = computeStats(entries, ledger?.min_balance_alert, ledger?.max_balance_alert);
  const horizonLabel = horizonOptions.find(h => h.value === horizon)?.label || horizon;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-[1800px] mx-auto px-2 sm:px-4 lg:px-6">
          <div className="flex items-center h-16">
            <div className="flex items-center gap-3 flex-shrink-0">
              <img 
                src="/assets/logo.png" 
                alt="Cash Forth Logo" 
                className="h-10 w-10 object-contain"
                data-testid="app-logo"
              />
              <div>
                <h1 className="font-heading font-bold text-lg tracking-tight" data-testid="app-title">
                  Cash Forth
                </h1>
                <p className="text-xs text-muted-foreground hidden sm:block">
                  Cash Flow Forecast + Unlimited What Ifs
                </p>
              </div>
              {/* Shared indicator */}
              {workspace && workspace.member_count > 1 && (
                <Badge variant="secondary" className="ml-2 text-xs hidden sm:flex">
                  <Users className="w-3 h-3 mr-1" />
                  Shared ({workspace.member_count})
                </Badge>
              )}
            </div>

            <div className="flex items-center gap-2 sm:gap-3 ml-auto">
              <div className="hidden sm:block">
                <CurrencySelect />
              </div>
              <Select value={horizon} onValueChange={(value) => {
                setHorizon(value);
                analytics.trackTimeHorizonSelected(value);
              }}>
                <SelectTrigger className="w-[100px] sm:w-[140px]" data-testid="horizon-select">
                  <SelectValue placeholder="Time horizon" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 Days</SelectItem>
                  <SelectItem value="90">90 Days</SelectItem>
                  <SelectItem value="180">6 Months</SelectItem>
                  <SelectItem value="365">1 Year</SelectItem>
                  <SelectItem value="730">2 Years</SelectItem>
                </SelectContent>
              </Select>

              <Button
                variant="outline"
                size="icon"
                onClick={() => setShowSettings(true)}
                data-testid="settings-btn"
                className="hidden sm:flex"
              >
                <Settings className="w-4 h-4" />
              </Button>

              {/* Share/Workspace Button */}
              <Button
                variant="outline"
                size="icon"
                onClick={() => setShowWorkspace(true)}
                data-testid="share-btn"
                className="hidden sm:flex relative"
              >
                <Users className="w-4 h-4" />
                {workspace && workspace.member_count > 1 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary text-primary-foreground rounded-full text-[10px] flex items-center justify-center">
                    {workspace.member_count}
                  </span>
                )}
              </Button>

              {/* View Toggle */}
              <div className="hidden sm:flex items-center border border-input rounded-md overflow-hidden">
                <Button
                  variant={viewMode === 'table' ? 'default' : 'ghost'}
                  size="icon"
                  className="rounded-none h-9 w-9"
                  onClick={() => {
                    setViewMode('table');
                    analytics.trackForecastViewSelected('ledger');
                  }}
                  data-testid="table-view-btn"
                >
                  <TableIcon className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'chart' ? 'default' : 'ghost'}
                  size="icon"
                  className="rounded-none h-9 w-9"
                  onClick={() => {
                    setViewMode('chart');
                    analytics.trackForecastViewSelected('chart');
                  }}
                  data-testid="chart-view-btn"
                >
                  <LineChart className="w-4 h-4" />
                </Button>
              </div>

              <Button
                variant="outline"
                size="icon"
                onClick={() => setShowTransactionList(true)}
                data-testid="list-btn"
                className="hidden sm:flex"
              >
                <List className="w-4 h-4" />
              </Button>


              <div className="hidden sm:flex items-center gap-1">
                <Button
                  variant="outline"
                  onClick={() => setShowImportConfirm(true)}
                  data-testid="import-csv-btn"
                >
                  <Upload className="w-4 h-4 mr-2" /> Import
                </Button>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button className="p-1 text-muted-foreground hover:text-foreground transition-colors" data-testid="import-info-tooltip">
                        <Info className="w-4 h-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs">
                      <p>Optional - Import a CSV file from your bank to generate a forecast. It will likely need editing, but can be a good starting point.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              <Button
                onClick={() => {
                  setEditingTransaction(null);
                  setShowTransactionForm(true);
                }}
                data-testid="add-transaction-btn"
                size="icon"
                className="sm:size-default sm:w-auto sm:px-4"
              >
                <Plus className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Add</span>
              </Button>

              {/* Mobile-only overflow menu — exposes the controls hidden at <sm. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild className="sm:hidden">
                  <Button variant="outline" size="icon" data-testid="mobile-more-menu-btn">
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={() => setShowSettings(true)} data-testid="mobile-menu-settings">
                    <Settings className="w-4 h-4 mr-2" /> Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowWorkspace(true)} data-testid="mobile-menu-workspace">
                    <Users className="w-4 h-4 mr-2" /> Workspace
                    {workspace && workspace.member_count > 1 && (
                      <span className="ml-auto text-xs text-muted-foreground">{workspace.member_count}</span>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowTransactionList(true)} data-testid="mobile-menu-transactions">
                    <List className="w-4 h-4 mr-2" /> Transactions
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">View</DropdownMenuLabel>
                  <DropdownMenuItem
                    onClick={() => { setViewMode('table'); analytics.trackForecastViewSelected('ledger'); }}
                    data-testid="mobile-menu-table-view"
                  >
                    <TableIcon className="w-4 h-4 mr-2" />
                    Table {viewMode === 'table' && '✓'}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => { setViewMode('chart'); analytics.trackForecastViewSelected('chart'); }}
                    data-testid="mobile-menu-chart-view"
                  >
                    <LineChart className="w-4 h-4 mr-2" />
                    Chart {viewMode === 'chart' && '✓'}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setShowImportConfirm(true)} data-testid="mobile-menu-import">
                    <Upload className="w-4 h-4 mr-2" /> Import CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={toggleTheme} data-testid="mobile-menu-theme">
                    {theme === 'dark' ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
                    {theme === 'dark' ? 'Light mode' : 'Dark mode'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                variant="outline"
                size="icon"
                onClick={toggleTheme}
                data-testid="theme-toggle-btn"
                className="hidden sm:flex"
              >
                {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </Button>

              {/* User Account Button — custom "Manage Subscription" added
                  above Clerk's defaults (Manage Account / Sign Out). */}
              <UserButton 
                afterSignOutUrl="/"
                appearance={{
                  elements: {
                    avatarBox: "w-9 h-9"
                  }
                }}
              >
                <UserButton.MenuItems>
                  <UserButton.Action
                    label="Manage Subscription"
                    labelIcon={<CreditCard className="w-4 h-4" />}
                    onClick={handleManageSubscription}
                  />
                </UserButton.MenuItems>
              </UserButton>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-2 sm:px-4 lg:px-6 py-6">
        {ledger && (
          <div className="mb-6 p-4 rounded-xl border bg-card flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4" data-testid="stats-summary">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-1 min-w-0">
              <div className="space-y-1 group">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Starting Balance</p>
                <div className="flex items-center gap-1.5">
                  <p className="font-mono text-lg sm:text-xl font-bold">{formatCurrency(ledger.starting_balance)}</p>
                  <button
                    onClick={() => setShowSettings(true)}
                    data-testid="edit-starting-balance-btn"
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
                    title="Edit starting balance and alert thresholds"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">as of {ledger.as_of_date}</p>
              </div>

              {stats && (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                    <TrendingDown className="w-3 h-3" /> Projected Low
                  </p>
                  <p className={`font-mono text-lg sm:text-xl font-bold ${stats.minBalance < (ledger.min_balance_alert || -Infinity) ? 'text-destructive' : ''}`}>
                    {formatCurrency(stats.minBalance)}
                  </p>
                  {stats.lowAlertCount > 0 && (
                    <Badge variant="destructive" className="text-xs">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      {stats.lowAlertCount} alerts
                    </Badge>
                  )}
                </div>
              )}

              {stats && (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> Projected High
                  </p>
                  <p className={`font-mono text-lg sm:text-xl font-bold ${stats.maxBalance > (ledger.max_balance_alert || Infinity) ? 'text-yellow-600 dark:text-yellow-400' : ''}`}>
                    {formatCurrency(stats.maxBalance)}
                  </p>
                  {stats.highAlertCount > 0 && (
                    <Badge className="text-xs bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 border-yellow-500/50">
                      {stats.highAlertCount} surplus
                    </Badge>
                  )}
                </div>
              )}

              <div className="space-y-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Transactions</p>
                <p className="font-mono text-lg sm:text-xl font-bold">{entries.length}</p>
                <p className="text-xs text-muted-foreground">in {horizonLabel}</p>
              </div>
            </div>
          </div>
        )}

        {ledger && (ledger.min_balance_alert || ledger.max_balance_alert) && (
          <div className="mb-4 flex flex-wrap gap-3 text-xs">
            {ledger.min_balance_alert && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-destructive/10 text-destructive">
                <AlertTriangle className="w-3 h-3" />
                Low alert: below {formatCurrency(ledger.min_balance_alert)}
              </div>
            )}
            {ledger.max_balance_alert && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-yellow-500/10 text-yellow-600 dark:text-yellow-400">
                <TrendingUp className="w-3 h-3" />
                High alert: above {formatCurrency(ledger.max_balance_alert)}
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-pulse text-muted-foreground">Loading forecast...</div>
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-state">
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
              <TrendingUp className="w-8 h-8 text-muted-foreground" />
            </div>
            <h2 className="font-heading text-xl font-semibold mb-2">No Transactions Yet</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Add income and expenses to see your cash flow forecast.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={() => setShowSettings(true)} variant="outline">
                <Settings className="w-4 h-4 mr-2" /> Set Starting Balance
              </Button>
              <Button onClick={() => setShowCSVImport(true)} variant="outline">
                <Upload className="w-4 h-4 mr-2" /> Import from CSV
              </Button>
              <Button onClick={() => setShowTransactionForm(true)}>
                <Plus className="w-4 h-4 mr-2" /> Add First Transaction
              </Button>
            </div>
          </div>
        ) : viewMode === 'chart' ? (
          <BalanceChart 
            entries={entries} 
            startingBalance={ledger?.starting_balance || 0}
            minAlert={ledger?.min_balance_alert}
            maxAlert={ledger?.max_balance_alert}
          />
        ) : (
          <LedgerTable entries={entries} horizonDays={parseInt(horizon, 10)} transactions={transactions} onEditTransaction={openEditForm} />
        )}
      </main>

      {/* Footer */}
      <footer className="py-4 text-center text-xs text-muted-foreground border-t border-border/50">
        © 2026 Cash Forth. All rights reserved.
        <span className="mx-2">·</span>
        <button 
          onClick={() => setShowTermsOfService(true)}
          className="hover:text-foreground hover:underline transition-colors"
          data-testid="terms-of-service-link"
        >
          Terms of Service
        </button>
        <span className="mx-2">·</span>
        <button 
          onClick={() => setShowPrivacyPolicy(true)}
          className="hover:text-foreground hover:underline transition-colors"
          data-testid="privacy-policy-link"
        >
          Privacy Policy
        </button>
        <span className="mx-2">·</span>
        <button 
          onClick={() => setShowContactDialog(true)}
          className="hover:text-foreground hover:underline transition-colors"
          data-testid="contact-us-link"
        >
          Contact Us
        </button>
        <span className="mx-2">·</span>
        <a
          href="/learn"
          className="hover:text-foreground hover:underline transition-colors"
          data-testid="footer-about-link"
        >
          About
        </a>
      </footer>

      <Button
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-lg sm:hidden"
        onClick={() => {
          setEditingTransaction(null);
          setShowTransactionForm(true);
        }}
        data-testid="mobile-add-btn"
      >
        <Plus className="w-6 h-6" />
      </Button>

      <TransactionForm
        open={showTransactionForm}
        onOpenChange={(open) => {
          setShowTransactionForm(open);
          if (!open) setEditingTransaction(null);
        }}
        onSubmit={editingTransaction ? handleUpdateTransaction : handleCreateTransaction}
        onDelete={handleDeleteTransaction}
        initialData={editingTransaction}
      />

      <TransactionList
        open={showTransactionList}
        onOpenChange={setShowTransactionList}
        transactions={transactions}
        onEdit={openEditForm}
        onDelete={handleDeleteTransaction}
        onPause={handlePauseTransaction}
        onResume={handleResumeTransaction}
      />

      <SettingsPanel
        open={showSettings}
        onOpenChange={setShowSettings}
        onSettingsUpdate={loadData}
      />

      <WorkspacePanel
        open={showWorkspace}
        onOpenChange={setShowWorkspace}
      />

      <CSVImportWizard
        open={showCSVImport}
        onOpenChange={setShowCSVImport}
        onComplete={loadData}
      />

      <PrivacyPolicy
        open={showPrivacyPolicy}
        onOpenChange={setShowPrivacyPolicy}
      />

      <ContactDialog
        open={showContactDialog}
        onOpenChange={setShowContactDialog}
      />

      <TermsOfService
        open={showTermsOfService}
        onOpenChange={setShowTermsOfService}
      />

      {/* Import confirmation dialog */}
      <AlertDialog open={showImportConfirm} onOpenChange={setShowImportConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Import CSV File</AlertDialogTitle>
            <AlertDialogDescription>
              Warning: Generating a new forecast using a CSV file will replace any existing transactions. Do you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              setShowImportConfirm(false);
              setShowCSVImport(true);
            }}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Toaster position="bottom-center" />
    </div>
  );
}

// Auth Landing Page for signed out users
function AuthPage() {
  const { theme, toggleTheme } = useTheme();
  const [authMode, setAuthMode] = useState('signin'); // 'signin' or 'signup'

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img 
                src="/assets/logo.png" 
                alt="Cash Forth Logo" 
                className="h-10 w-10 object-contain"
                data-testid="app-logo"
              />
              <div>
                <h1 className="font-heading font-bold text-lg tracking-tight">
                  Cash Forth
                </h1>
                <p className="text-xs text-muted-foreground">
                  Cash Flow Forecast + Unlimited What Ifs
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={toggleTheme}
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Auth Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {authMode === 'signin' ? (
            <div className="space-y-4">
              <SignIn 
                routing="hash"
                appearance={{
                  elements: {
                    rootBox: "w-full",
                    card: "shadow-none border border-border rounded-lg"
                  }
                }}
              />
              <p className="text-center text-sm text-muted-foreground">
                Don't have an account?{' '}
                <button 
                  onClick={function() { setAuthMode('signup'); }}
                  className="text-primary hover:underline font-medium"
                >
                  Sign up
                </button>
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <SignUp 
                routing="hash"
                appearance={{
                  elements: {
                    rootBox: "w-full",
                    card: "shadow-none border border-border rounded-lg"
                  }
                }}
              />
              <p className="text-center text-sm text-muted-foreground">
                Already have an account?{' '}
                <button 
                  onClick={function() { setAuthMode('signin'); }}
                  className="text-primary hover:underline font-medium"
                >
                  Sign in
                </button>
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-4 text-center text-xs text-muted-foreground border-t border-border/50">
        © 2026 Cash Forth. All rights reserved.
        <span className="mx-2">·</span>
        <button 
          onClick={() => setShowTermsOfService(true)}
          className="hover:text-foreground hover:underline transition-colors"
          data-testid="terms-of-service-link"
        >
          Terms of Service
        </button>
        <span className="mx-2">·</span>
        <button 
          onClick={() => setShowPrivacyPolicy(true)}
          className="hover:text-foreground hover:underline transition-colors"
          data-testid="privacy-policy-link"
        >
          Privacy Policy
        </button>
        <span className="mx-2">·</span>
        <button 
          onClick={() => setShowContactDialog(true)}
          className="hover:text-foreground hover:underline transition-colors"
          data-testid="contact-us-link"
        >
          Contact Us
        </button>
        <span className="mx-2">·</span>
        <a
          href="/learn"
          className="hover:text-foreground hover:underline transition-colors"
          data-testid="footer-about-link"
        >
          About
        </a>
      </footer>
    </div>
  );
}

// Development mode dashboard - works without Clerk authentication (uses localStorage like guest mode)
function DevModeDashboard() {
  const { theme, toggleTheme } = useTheme();
  const [currencyCode] = useCurrency();
  const formatCurrency = (v) => fmtCurrency(v, currencyCode);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeMode, setUpgradeMode] = useState('save');
  const openUpgrade = (mode) => {
    setUpgradeMode(mode || 'save');
    setUpgradeOpen(true);
  };
  const [ledger, setLedger] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [horizon, setHorizon] = useState('90');
  const [loading, setLoading] = useState(true);

  const [showTransactionForm, setShowTransactionForm] = useState(false);
  const [showTransactionList, setShowTransactionList] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showCSVImport, setShowCSVImport] = useState(false);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [showTermsOfService, setShowTermsOfService] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [viewMode, setViewMode] = useState('table');

  const loadData = useCallback(() => {
    setLoading(true);
    try {
      const horizonDays = parseInt(horizon, 10);
      const ledgerData = getGuestLedger(horizonDays);
      const txData = getGuestTransactions();
      setLedger(ledgerData);
      setTransactions(txData);
    } catch (err) {
      console.error('Failed to load data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [horizon]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateTransaction = (data) => {
    try {
      createGuestTransaction(data);
      toast.success('Transaction added');
      analytics.trackTransactionCreated(data);
      analytics.trackForecastUpdated(transactions.length + 1, 'manual_transaction');
      loadData();
    } catch (err) {
      console.error('Failed to create:', err);
      toast.error('Failed to add transaction');
    }
  };

  const handleUpdateTransaction = (data) => {
    if (!editingTransaction) return;
    try {
      updateGuestTransaction(editingTransaction.id, data);
      toast.success('Transaction updated');
      analytics.trackTransactionEdited(data, Object.keys(data));
      setEditingTransaction(null);
      loadData();
    } catch (err) {
      console.error('Failed to update:', err);
      toast.error('Failed to update transaction');
    }
  };

  const handleDeleteTransaction = (id) => {
    try {
      deleteGuestTransaction(id);
      toast.success('Transaction deleted');
      loadData();
    } catch (err) {
      console.error('Failed to delete:', err);
      toast.error('Failed to delete transaction');
    }
  };

  const handlePauseTransaction = (id) => {
    try {
      pauseGuestTransaction(id);
      toast.success('Transaction paused');
      loadData();
    } catch (err) {
      console.error('Failed to pause:', err);
      toast.error('Failed to pause transaction');
    }
  };

  const handleResumeTransaction = (id) => {
    try {
      resumeGuestTransaction(id);
      toast.success('Transaction resumed');
      loadData();
    } catch (err) {
      console.error('Failed to resume:', err);
      toast.error('Failed to resume transaction');
    }
  };

  const openEditForm = (tx) => {
    setEditingTransaction(tx);
    setShowTransactionList(false);
    setShowTransactionForm(true);
  };

  const entries = ledger?.entries || [];
  const stats = computeStats(entries, ledger?.min_balance_alert, ledger?.max_balance_alert);
  const horizonLabel = horizonOptions.find(h => h.value === horizon)?.label || horizon;

  return (
    <div className="min-h-screen bg-background">
      {/* Permanent Guest Mode Banner — preview/dev variant */}
      <div
        className="bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 border-b border-primary/20"
        data-testid="guest-banner"
      >
        <div className="max-w-[1800px] mx-auto px-2 sm:px-4 lg:px-6 py-3">
          <div className="flex items-center justify-center gap-4">
            <Sparkles className="w-5 h-5 text-primary flex-shrink-0" />
            <p className="text-sm font-medium text-center">
              You're in guest mode. <span className="text-primary">Upgrade to save your forecast.</span>
            </p>
            <Button
              size="sm"
              onClick={() => {
                analytics.trackUpgradeClicked({ location: 'guest_banner', ctaLabel: 'Upgrade' });
                openUpgrade('save');
              }}
              data-testid="guest-banner-upgrade-btn"
              className="bg-black text-white hover:bg-black/85"
            >
              Upgrade
            </Button>
          </div>
        </div>
      </div>

      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-[1800px] mx-auto px-2 sm:px-4 lg:px-6">
          <div className="flex items-center h-16">
            <div className="flex items-center gap-3 flex-shrink-0">
              <img 
                src="/assets/logo.png" 
                alt="Cash Forth Logo" 
                className="h-10 w-10 object-contain"
                data-testid="app-logo"
              />
              <div>
                <h1 className="font-heading font-bold text-lg tracking-tight" data-testid="app-title">
                  Cash Forth
                </h1>
                <p className="text-xs text-muted-foreground hidden sm:block">
                  Cash Flow Forecast + Unlimited What Ifs
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 ml-auto">
              {/* Dev Mode Banner */}
              <Badge variant="outline" className="hidden sm:inline-flex text-xs bg-amber-500/10 text-amber-600 border-amber-500/50">
                Preview Mode
              </Badge>

              <div className="hidden sm:block">
                <CurrencySelect />
              </div>
              <Select value={horizon} onValueChange={(value) => {
                setHorizon(value);
                analytics.trackTimeHorizonSelected(value);
              }}>
                <SelectTrigger className="w-[100px] sm:w-[140px]" data-testid="horizon-select">
                  <SelectValue placeholder="Time horizon" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 Days</SelectItem>
                  <SelectItem value="90">90 Days</SelectItem>
                  <SelectItem value="180">6 Months</SelectItem>
                  <SelectItem value="365">1 Year</SelectItem>
                  <SelectItem value="730">2 Years</SelectItem>
                </SelectContent>
              </Select>

              <Button
                variant="outline"
                size="icon"
                onClick={() => setShowSettings(true)}
                data-testid="settings-btn"
                className="hidden sm:flex"
              >
                <Settings className="w-4 h-4" />
              </Button>

              <div className="hidden sm:flex items-center border border-input rounded-md overflow-hidden">
                <Button
                  variant={viewMode === 'table' ? 'default' : 'ghost'}
                  size="icon"
                  className="rounded-none h-9 w-9"
                  onClick={() => {
                    setViewMode('table');
                    analytics.trackForecastViewSelected('ledger');
                  }}
                  data-testid="table-view-btn"
                >
                  <TableIcon className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'chart' ? 'default' : 'ghost'}
                  size="icon"
                  className="rounded-none h-9 w-9"
                  onClick={() => {
                    setViewMode('chart');
                    analytics.trackForecastViewSelected('chart');
                  }}
                  data-testid="chart-view-btn"
                >
                  <LineChart className="w-4 h-4" />
                </Button>
              </div>

              <Button
                variant="outline"
                size="icon"
                onClick={() => setShowTransactionList(true)}
                data-testid="list-btn"
                className="hidden sm:flex"
              >
                <List className="w-4 h-4" />
              </Button>

              <div className="hidden sm:flex items-center gap-1">
                <Button
                  variant="outline"
                  onClick={() => setShowImportConfirm(true)}
                  data-testid="import-csv-btn"
                >
                  <Upload className="w-4 h-4 mr-2" /> Import
                </Button>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button className="p-1 text-muted-foreground hover:text-foreground transition-colors" data-testid="import-info-tooltip">
                        <Info className="w-4 h-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs">
                      <p>Optional - Import a CSV file from your bank to generate a forecast. It will likely need editing, but can be a good starting point.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              <Button
                onClick={() => {
                  setEditingTransaction(null);
                  setShowTransactionForm(true);
                }}
                data-testid="add-transaction-btn"
                size="icon"
                className="sm:size-default sm:w-auto sm:px-4"
              >
                <Plus className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Add</span>
              </Button>

              {/* Mobile-only overflow menu — exposes the controls hidden at <sm. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild className="sm:hidden">
                  <Button variant="outline" size="icon" data-testid="mobile-more-menu-btn">
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={() => setShowSettings(true)} data-testid="mobile-menu-settings">
                    <Settings className="w-4 h-4 mr-2" /> Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowTransactionList(true)} data-testid="mobile-menu-transactions">
                    <List className="w-4 h-4 mr-2" /> Transactions
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">View</DropdownMenuLabel>
                  <DropdownMenuItem
                    onClick={() => { setViewMode('table'); analytics.trackForecastViewSelected('ledger'); }}
                    data-testid="mobile-menu-table-view"
                  >
                    <TableIcon className="w-4 h-4 mr-2" />
                    Table {viewMode === 'table' && '✓'}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => { setViewMode('chart'); analytics.trackForecastViewSelected('chart'); }}
                    data-testid="mobile-menu-chart-view"
                  >
                    <LineChart className="w-4 h-4 mr-2" />
                    Chart {viewMode === 'chart' && '✓'}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setShowImportConfirm(true)} data-testid="mobile-menu-import">
                    <Upload className="w-4 h-4 mr-2" /> Import CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={toggleTheme} data-testid="mobile-menu-theme">
                    {theme === 'dark' ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
                    {theme === 'dark' ? 'Light mode' : 'Dark mode'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                variant="outline"
                size="icon"
                onClick={toggleTheme}
                data-testid="theme-toggle-btn"
                className="hidden sm:flex"
              >
                {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </Button>

              {/* Sign In Button — preview/dev variant; production GuestDashboard has the real Clerk-wired button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => { window.location.href = '/app'; }}
                data-testid="guest-header-sign-in-btn"
                className="flex"
              >
                <LogIn className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Sign In</span>
              </Button>

            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-2 sm:px-4 lg:px-6 py-6">
        {ledger && (
          <div className="mb-6 p-4 rounded-xl border bg-card flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4" data-testid="stats-summary">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-1 min-w-0">
              <div className="space-y-1 group">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Starting Balance</p>
                <div className="flex items-center gap-1.5">
                  <p className="font-mono text-lg sm:text-xl font-bold">{formatCurrency(ledger.starting_balance)}</p>
                  <button
                    onClick={() => setShowSettings(true)}
                    data-testid="edit-starting-balance-btn"
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
                    title="Edit starting balance and alert thresholds"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">as of {ledger.as_of_date}</p>
              </div>

              {stats && (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                    <TrendingDown className="w-3 h-3" /> Projected Low
                  </p>
                  <p className={`font-mono text-lg sm:text-xl font-bold ${stats.minBalance < (ledger.min_balance_alert || -Infinity) ? 'text-destructive' : ''}`}>
                    {formatCurrency(stats.minBalance)}
                  </p>
                  {stats.lowAlertCount > 0 && (
                    <Badge variant="destructive" className="text-xs">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      {stats.lowAlertCount} alerts
                    </Badge>
                  )}
                </div>
              )}

              {stats && (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> Projected High
                  </p>
                  <p className={`font-mono text-lg sm:text-xl font-bold ${stats.maxBalance > (ledger.max_balance_alert || Infinity) ? 'text-yellow-600 dark:text-yellow-400' : ''}`}>
                    {formatCurrency(stats.maxBalance)}
                  </p>
                  {stats.highAlertCount > 0 && (
                    <Badge className="text-xs bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 border-yellow-500/50">
                      {stats.highAlertCount} surplus
                    </Badge>
                  )}
                </div>
              )}

              <div className="space-y-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Transactions</p>
                <p className="font-mono text-lg sm:text-xl font-bold">{entries.length}</p>
                <p className="text-xs text-muted-foreground">in {horizonLabel}</p>
              </div>
            </div>

            <Button
              onClick={() => {
                analytics.trackUpgradeClicked({ location: 'save_forecast', ctaLabel: 'Save Forecast' });
                openUpgrade('save');
              }}
              data-testid="save-forecast-btn"
              className="flex-shrink-0 w-full sm:w-auto bg-black text-white hover:bg-black/85"
            >
              Save Forecast
            </Button>

          </div>
        )}

        {ledger && (ledger.min_balance_alert || ledger.max_balance_alert) && (
          <div className="mb-4 flex flex-wrap gap-3 text-xs">
            {ledger.min_balance_alert && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-destructive/10 text-destructive">
                <AlertTriangle className="w-3 h-3" />
                Low alert: below {formatCurrency(ledger.min_balance_alert)}
              </div>
            )}
            {ledger.max_balance_alert && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-yellow-500/10 text-yellow-600 dark:text-yellow-400">
                <TrendingUp className="w-3 h-3" />
                High alert: above {formatCurrency(ledger.max_balance_alert)}
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-pulse text-muted-foreground">Loading forecast...</div>
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-state">
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
              <TrendingUp className="w-8 h-8 text-muted-foreground" />
            </div>
            <h2 className="font-heading text-xl font-semibold mb-2">No Transactions Yet</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Add income and expenses to see your cash flow forecast.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={() => setShowSettings(true)} variant="outline">
                <Settings className="w-4 h-4 mr-2" /> Set Starting Balance
              </Button>
              <Button onClick={() => setShowCSVImport(true)} variant="outline">
                <Upload className="w-4 h-4 mr-2" /> Import from CSV
              </Button>
              <Button onClick={() => setShowTransactionForm(true)}>
                <Plus className="w-4 h-4 mr-2" /> Add First Transaction
              </Button>
            </div>
          </div>
        ) : viewMode === 'chart' ? (
          <BalanceChart 
            entries={entries} 
            startingBalance={ledger?.starting_balance || 0}
            minAlert={ledger?.min_balance_alert}
            maxAlert={ledger?.max_balance_alert}
          />
        ) : (
          <LedgerTable entries={entries} horizonDays={parseInt(horizon, 10)} transactions={transactions} onEditTransaction={openEditForm} />
        )}
      </main>

      <footer className="py-4 text-center text-xs text-muted-foreground border-t border-border/50">
        © 2026 Cash Forth. All rights reserved.
        <span className="mx-2">·</span>
        <button 
          onClick={() => setShowTermsOfService(true)}
          className="hover:text-foreground hover:underline transition-colors"
          data-testid="terms-of-service-link"
        >
          Terms of Service
        </button>
        <span className="mx-2">·</span>
        <button 
          onClick={() => setShowPrivacyPolicy(true)}
          className="hover:text-foreground hover:underline transition-colors"
          data-testid="privacy-policy-link"
        >
          Privacy Policy
        </button>
        <span className="mx-2">·</span>
        <button 
          onClick={() => setShowContactDialog(true)}
          className="hover:text-foreground hover:underline transition-colors"
          data-testid="contact-us-link"
        >
          Contact Us
        </button>
        <span className="mx-2">·</span>
        <a
          href="/learn"
          className="hover:text-foreground hover:underline transition-colors"
          data-testid="footer-about-link"
        >
          About
        </a>
      </footer>

      <CSVImportWizard
        open={showCSVImport}
        onOpenChange={setShowCSVImport}
        onComplete={loadData}
      />

      <Button
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-lg sm:hidden"
        onClick={() => {
          setEditingTransaction(null);
          setShowTransactionForm(true);
        }}
        data-testid="mobile-add-btn"
      >
        <Plus className="w-6 h-6" />
      </Button>

      <TransactionForm
        open={showTransactionForm}
        onOpenChange={(open) => {
          setShowTransactionForm(open);
          if (!open) setEditingTransaction(null);
        }}
        onSubmit={editingTransaction ? handleUpdateTransaction : handleCreateTransaction}
        onDelete={handleDeleteTransaction}
        initialData={editingTransaction}
        isGuestMode={true}
      />

      <TransactionList
        open={showTransactionList}
        onOpenChange={setShowTransactionList}
        transactions={transactions}
        onEdit={openEditForm}
        onDelete={handleDeleteTransaction}
        onPause={handlePauseTransaction}
        onResume={handleResumeTransaction}
      />

      <SettingsPanel
        open={showSettings}
        onOpenChange={setShowSettings}
        onSettingsUpdate={loadData}
        isGuestMode={true}
      />

      <PrivacyPolicy
        open={showPrivacyPolicy}
        onOpenChange={setShowPrivacyPolicy}
      />

      <ContactDialog
        open={showContactDialog}
        onOpenChange={setShowContactDialog}
      />

      <TermsOfService
        open={showTermsOfService}
        onOpenChange={setShowTermsOfService}
      />

      {/* Import confirmation dialog */}
      <AlertDialog open={showImportConfirm} onOpenChange={setShowImportConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Import CSV File</AlertDialogTitle>
            <AlertDialogDescription>
              Warning: Generating a new forecast using a CSV file will replace any existing transactions. Do you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              setShowImportConfirm(false);
              setShowCSVImport(true);
            }}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Toaster position="bottom-center" />

      <UpgradeModal
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        mode={upgradeMode}
        devMode
      />
    </div>
  );
}

// Guest Dashboard - for users who haven't signed up yet
function GuestDashboard() {
  const { theme, toggleTheme } = useTheme();
  const { openSignUp, openSignIn } = useClerk();
  // We may render GuestDashboard for an already-signed-in (but unsubscribed)
  // user, in which case openSignIn/openSignUp are no-ops. We need to detect
  // that and route them to /app instead.
  const { isLoaded: authLoaded, isSignedIn: alreadySignedIn } = useAuth();
  const [currencyCode] = useCurrency();
  const formatCurrency = (v) => fmtCurrency(v, currencyCode);
  const [ledger, setLedger] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [horizon, setHorizon] = useState('90');
  const [loading, setLoading] = useState(true);
  const [showBanner, setShowBanner] = useState(!isBannerDismissed());

  useDocumentMeta({
    title: 'Try Cash Forth — Free Cash Flow Forecast',
    description: 'Start forecasting your cash flow in seconds. No account needed. Try Cash Forth in guest mode and save your forecast later by upgrading.',
  });

  const [showTransactionForm, setShowTransactionForm] = useState(false);
  const [showTransactionList, setShowTransactionList] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showImportAccountRequired, setShowImportAccountRequired] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [showTermsOfService, setShowTermsOfService] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeMode, setUpgradeMode] = useState('save');

  const openUpgrade = (mode) => {
    setUpgradeMode(mode || 'save');
    setUpgradeOpen(true);
  };

  const loadData = useCallback(() => {
    setLoading(true);
    try {
      const horizonDays = parseInt(horizon, 10);
      const ledgerData = getGuestLedger(horizonDays);
      const txData = getGuestTransactions();
      setLedger(ledgerData);
      setTransactions(txData);
    } catch (err) {
      console.error('Failed to load guest data:', err);
    } finally {
      setLoading(false);
    }
  }, [horizon]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Check if banner should reappear
  useEffect(() => {
    if (!isBannerDismissed()) {
      setShowBanner(true);
    }
  }, [transactions.length]);

  const handleCreateTransaction = (data) => {
    try {
      createGuestTransaction(data);
      toast.success('Transaction added');
      analytics.trackTransactionCreated(data);
      analytics.trackForecastUpdated(transactions.length + 1, 'manual_transaction');
      loadData();
      // Check if banner should reappear after adding transaction
      if (!isBannerDismissed()) {
        setShowBanner(true);
      }
    } catch (err) {
      console.error('Failed to create:', err);
      toast.error('Failed to add transaction');
    }
  };

  const handleUpdateTransaction = (data) => {
    if (!editingTransaction) return;
    try {
      updateGuestTransaction(editingTransaction.id, data);
      toast.success('Transaction updated');
      analytics.trackTransactionEdited(data, Object.keys(data));
      setEditingTransaction(null);
      loadData();
    } catch (err) {
      console.error('Failed to update:', err);
      toast.error('Failed to update transaction');
    }
  };

  const handleDeleteTransaction = (id) => {
    try {
      deleteGuestTransaction(id);
      toast.success('Transaction deleted');
      loadData();
    } catch (err) {
      console.error('Failed to delete:', err);
      toast.error('Failed to delete transaction');
    }
  };

  const handlePauseTransaction = (id) => {
    try {
      pauseGuestTransaction(id);
      toast.success('Transaction paused');
      loadData();
    } catch (err) {
      console.error('Failed to pause:', err);
      toast.error('Failed to pause transaction');
    }
  };

  const handleResumeTransaction = (id) => {
    try {
      resumeGuestTransaction(id);
      toast.success('Transaction resumed');
      loadData();
    } catch (err) {
      console.error('Failed to resume:', err);
      toast.error('Failed to resume transaction');
    }
  };

  const openEditForm = (tx) => {
    setEditingTransaction(tx);
    setShowTransactionList(false);
    setShowTransactionForm(true);
  };

  const handleDismissBanner = () => {
    dismissBanner();
    setShowBanner(false);
  };

  const handleSignUp = () => {
    if (authLoaded && alreadySignedIn) {
      window.location.href = '/app';
      return;
    }
    openSignUp({});
  };

  const handleSignIn = () => {
    if (authLoaded && alreadySignedIn) {
      window.location.href = '/app';
      return;
    }
    openSignIn({});
  };

  const entries = ledger?.entries || [];
  const stats = computeStats(entries, ledger?.min_balance_alert, ledger?.max_balance_alert);
  const horizonLabel = horizonOptions.find(h => h.value === horizon)?.label || horizon;

  return (
    <div className="min-h-screen bg-background">
      {/* Permanent Guest Mode Banner — no dismiss; Upgrade CTA opens save-mode modal. */}
      <div
        className="bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 border-b border-primary/20"
        data-testid="guest-banner"
      >
        <div className="max-w-[1800px] mx-auto px-2 sm:px-4 lg:px-6 py-3">
          <div className="flex items-center justify-center gap-4">
            <Sparkles className="w-5 h-5 text-primary flex-shrink-0" />
            <p className="text-sm font-medium text-center">
              You're in guest mode. <span className="text-primary">Upgrade to save your forecast.</span>
            </p>
            <Button
              size="sm"
              onClick={() => {
                analytics.trackUpgradeClicked({ location: 'guest_banner', ctaLabel: 'Upgrade' });
                openUpgrade('save');
              }}
              data-testid="guest-banner-upgrade-btn"
              className="bg-black text-white hover:bg-black/85"
            >
              Upgrade
            </Button>
          </div>
        </div>
      </div>

      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-[1800px] mx-auto px-2 sm:px-4 lg:px-6">
          <div className="flex items-center h-16">
            <div className="flex items-center gap-3 flex-shrink-0">
              <img 
                src="/assets/logo.png" 
                alt="Cash Forth Logo" 
                className="h-10 w-10 object-contain"
                data-testid="app-logo"
              />
              <div>
                <h1 className="font-heading font-bold text-lg tracking-tight" data-testid="app-title">
                  Cash Forth
                </h1>
                <p className="text-xs text-muted-foreground hidden sm:block">
                  Cash Flow Forecast + Unlimited What Ifs
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 ml-auto">
              <Badge variant="outline" className="hidden sm:inline-flex text-xs bg-amber-500/10 text-amber-600 border-amber-500/50">
                Guest
              </Badge>

              <div className="hidden sm:block">
                <CurrencySelect />
              </div>
              <Select value={horizon} onValueChange={(value) => {
                setHorizon(value);
                analytics.trackTimeHorizonSelected(value);
              }}>
                <SelectTrigger className="w-[100px] sm:w-[140px]" data-testid="horizon-select">
                  <SelectValue placeholder="Time horizon" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 Days</SelectItem>
                  <SelectItem value="90">90 Days</SelectItem>
                  <SelectItem value="180">6 Months</SelectItem>
                  <SelectItem value="365">1 Year</SelectItem>
                  <SelectItem value="730">2 Years</SelectItem>
                </SelectContent>
              </Select>

              <Button
                variant="outline"
                size="icon"
                onClick={() => setShowSettings(true)}
                data-testid="settings-btn"
                className="hidden sm:flex"
              >
                <Settings className="w-4 h-4" />
              </Button>

              <div className="hidden sm:flex items-center border border-input rounded-md overflow-hidden">
                <Button
                  variant={viewMode === 'table' ? 'default' : 'ghost'}
                  size="icon"
                  className="rounded-none h-9 w-9"
                  onClick={() => {
                    setViewMode('table');
                    analytics.trackForecastViewSelected('ledger');
                  }}
                  data-testid="table-view-btn"
                >
                  <TableIcon className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'chart' ? 'default' : 'ghost'}
                  size="icon"
                  className="rounded-none h-9 w-9"
                  onClick={() => {
                    setViewMode('chart');
                    analytics.trackForecastViewSelected('chart');
                  }}
                  data-testid="chart-view-btn"
                >
                  <LineChart className="w-4 h-4" />
                </Button>
              </div>

              <Button
                variant="outline"
                size="icon"
                onClick={() => setShowTransactionList(true)}
                data-testid="list-btn"
                className="hidden sm:flex"
              >
                <List className="w-4 h-4" />
              </Button>

              <div className="hidden sm:flex items-center gap-1">
                <Button
                  variant="outline"
                  onClick={() => setShowImportAccountRequired(true)}
                  data-testid="import-csv-btn"
                >
                  <Upload className="w-4 h-4 mr-2" /> Import
                </Button>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button className="p-1 text-muted-foreground hover:text-foreground transition-colors" data-testid="import-info-tooltip">
                        <Info className="w-4 h-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs">
                      <p>Optional - Import a CSV file from your bank to generate a forecast. It will likely need editing, but can be a good starting point.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              <Button
                onClick={() => {
                  setEditingTransaction(null);
                  setShowTransactionForm(true);
                }}
                data-testid="add-transaction-btn"
                size="icon"
                className="sm:size-default sm:w-auto sm:px-4"
              >
                <Plus className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Add</span>
              </Button>

              {/* Mobile-only overflow menu — exposes the controls hidden at <sm. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild className="sm:hidden">
                  <Button variant="outline" size="icon" data-testid="mobile-more-menu-btn">
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={() => setShowSettings(true)} data-testid="mobile-menu-settings">
                    <Settings className="w-4 h-4 mr-2" /> Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowTransactionList(true)} data-testid="mobile-menu-transactions">
                    <List className="w-4 h-4 mr-2" /> Transactions
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">View</DropdownMenuLabel>
                  <DropdownMenuItem
                    onClick={() => { setViewMode('table'); analytics.trackForecastViewSelected('ledger'); }}
                    data-testid="mobile-menu-table-view"
                  >
                    <TableIcon className="w-4 h-4 mr-2" />
                    Table {viewMode === 'table' && '✓'}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => { setViewMode('chart'); analytics.trackForecastViewSelected('chart'); }}
                    data-testid="mobile-menu-chart-view"
                  >
                    <LineChart className="w-4 h-4 mr-2" />
                    Chart {viewMode === 'chart' && '✓'}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setShowImportAccountRequired(true)} data-testid="mobile-menu-import">
                    <Upload className="w-4 h-4 mr-2" /> Import CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={toggleTheme} data-testid="mobile-menu-theme">
                    {theme === 'dark' ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
                    {theme === 'dark' ? 'Light mode' : 'Dark mode'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                variant="outline"
                size="icon"
                onClick={toggleTheme}
                data-testid="theme-toggle-btn"
                className="hidden sm:flex"
              >
                {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </Button>

              {/* Show UserButton (avatar + Sign Out menu) when actually
                  signed in; otherwise show the Sign In button. This page is
                  rendered for two distinct user states — true guests and
                  signed-in users without an active subscription — and they
                  need different identity controls. */}
              {authLoaded && alreadySignedIn ? (
                <UserButton
                  afterSignOutUrl="/"
                  appearance={{ elements: { avatarBox: "w-9 h-9" } }}
                />
              ) : (
                <Button variant="outline" size="sm" onClick={handleSignIn} className="flex" data-testid="guest-header-sign-in-btn">
                  <LogIn className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Sign In</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-2 sm:px-4 lg:px-6 py-6">
        {ledger && (
          <div className="mb-6 p-4 rounded-xl border bg-card flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4" data-testid="stats-summary">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-1 min-w-0">
              <div className="space-y-1 group">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Starting Balance</p>
                <div className="flex items-center gap-1.5">
                  <p className="font-mono text-lg sm:text-xl font-bold">{formatCurrency(ledger.starting_balance)}</p>
                  <button
                    onClick={() => setShowSettings(true)}
                    data-testid="edit-starting-balance-btn"
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
                    title="Edit starting balance and alert thresholds"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">as of {ledger.as_of_date}</p>
              </div>

              {stats && (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                    <TrendingDown className="w-3 h-3" /> Projected Low
                  </p>
                  <p className={`font-mono text-lg sm:text-xl font-bold ${stats.minBalance < (ledger.min_balance_alert || -Infinity) ? 'text-destructive' : ''}`}>
                    {formatCurrency(stats.minBalance)}
                  </p>
                  {stats.lowAlertCount > 0 && (
                    <Badge variant="destructive" className="text-xs">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      {stats.lowAlertCount} alerts
                    </Badge>
                  )}
                </div>
              )}

              {stats && (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> Projected High
                  </p>
                  <p className={`font-mono text-lg sm:text-xl font-bold ${stats.maxBalance > (ledger.max_balance_alert || Infinity) ? 'text-yellow-600 dark:text-yellow-400' : ''}`}>
                    {formatCurrency(stats.maxBalance)}
                  </p>
                  {stats.highAlertCount > 0 && (
                    <Badge className="text-xs bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 border-yellow-500/50">
                      {stats.highAlertCount} surplus
                    </Badge>
                  )}
                </div>
              )}

              <div className="space-y-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Transactions</p>
                <p className="font-mono text-lg sm:text-xl font-bold">{entries.length}</p>
                <p className="text-xs text-muted-foreground">in {horizonLabel}</p>
              </div>
            </div>

            <Button
              onClick={() => {
                analytics.trackUpgradeClicked({ location: 'save_forecast', ctaLabel: 'Save Forecast' });
                openUpgrade('save');
              }}
              data-testid="save-forecast-btn"
              className="flex-shrink-0 w-full sm:w-auto bg-black text-white hover:bg-black/85"
            >
              Save Forecast
            </Button>

          </div>
        )}

        {ledger && (ledger.min_balance_alert || ledger.max_balance_alert) && (
          <div className="mb-4 flex flex-wrap gap-3 text-xs">
            {ledger.min_balance_alert && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-destructive/10 text-destructive">
                <AlertTriangle className="w-3 h-3" />
                Low alert: below {formatCurrency(ledger.min_balance_alert)}
              </div>
            )}
            {ledger.max_balance_alert && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-yellow-500/10 text-yellow-600 dark:text-yellow-400">
                <TrendingUp className="w-3 h-3" />
                High alert: above {formatCurrency(ledger.max_balance_alert)}
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-pulse text-muted-foreground">Loading forecast...</div>
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-state">
            <h2 className="font-heading text-xl font-semibold mb-2">Welcome to Cash Forth!</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Try it out - add income and expenses to see your cash flow forecast. No account needed to get started.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={() => setShowSettings(true)} variant="outline">
                <Settings className="w-4 h-4 mr-2" /> Set Starting Balance
              </Button>
              <Button onClick={() => setShowTransactionForm(true)}>
                <Plus className="w-4 h-4 mr-2" /> Add First Transaction
              </Button>
            </div>
          </div>
        ) : viewMode === 'chart' ? (
          <BalanceChart 
            entries={entries} 
            startingBalance={ledger?.starting_balance || 0}
            minAlert={ledger?.min_balance_alert}
            maxAlert={ledger?.max_balance_alert}
          />
        ) : (
          <LedgerTable entries={entries} horizonDays={parseInt(horizon, 10)} transactions={transactions} onEditTransaction={openEditForm} />
        )}
      </main>

      <footer className="py-4 text-center text-xs text-muted-foreground border-t border-border/50">
        © 2026 Cash Forth. All rights reserved.
        <span className="mx-2">·</span>
        <button 
          onClick={() => setShowTermsOfService(true)}
          className="hover:text-foreground hover:underline transition-colors"
          data-testid="terms-of-service-link"
        >
          Terms of Service
        </button>
        <span className="mx-2">·</span>
        <button 
          onClick={() => setShowPrivacyPolicy(true)}
          className="hover:text-foreground hover:underline transition-colors"
          data-testid="privacy-policy-link"
        >
          Privacy Policy
        </button>
        <span className="mx-2">·</span>
        <button 
          onClick={() => setShowContactDialog(true)}
          className="hover:text-foreground hover:underline transition-colors"
          data-testid="contact-us-link"
        >
          Contact Us
        </button>
        <span className="mx-2">·</span>
        <a
          href="/learn"
          className="hover:text-foreground hover:underline transition-colors"
          data-testid="footer-about-link"
        >
          About
        </a>
      </footer>

      <Button
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-lg sm:hidden"
        onClick={() => {
          setEditingTransaction(null);
          setShowTransactionForm(true);
        }}
        data-testid="mobile-add-btn"
      >
        <Plus className="w-6 h-6" />
      </Button>

      <TransactionForm
        open={showTransactionForm}
        onOpenChange={(open) => {
          setShowTransactionForm(open);
          if (!open) setEditingTransaction(null);
        }}
        onSubmit={editingTransaction ? handleUpdateTransaction : handleCreateTransaction}
        onDelete={handleDeleteTransaction}
        initialData={editingTransaction}
        isGuestMode={true}
      />

      <TransactionList
        open={showTransactionList}
        onOpenChange={setShowTransactionList}
        transactions={transactions}
        onEdit={openEditForm}
        onDelete={handleDeleteTransaction}
        onPause={handlePauseTransaction}
        onResume={handleResumeTransaction}
      />

      <SettingsPanel
        open={showSettings}
        onOpenChange={setShowSettings}
        onSettingsUpdate={loadData}
        isGuestMode={true}
      />

      <PrivacyPolicy
        open={showPrivacyPolicy}
        onOpenChange={setShowPrivacyPolicy}
      />

      <ContactDialog
        open={showContactDialog}
        onOpenChange={setShowContactDialog}
      />

      <TermsOfService
        open={showTermsOfService}
        onOpenChange={setShowTermsOfService}
      />

      {/* Account required dialog for CSV import in guest mode */}
      <AlertDialog open={showImportAccountRequired} onOpenChange={setShowImportAccountRequired}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Account Required</AlertDialogTitle>
            <AlertDialogDescription>
              Create an account and subscribe to use this feature. It allows you to upload a CSV file from your bank and turn it into a forward looking forecast.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="import-account-required-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="import-account-required-upgrade-btn"
              onClick={() => {
                setShowImportAccountRequired(false);
                analytics.trackUpgradeClicked({ location: 'csv_import_gate', ctaLabel: 'Upgrade' });
                openUpgrade('pricing');
              }}
            >
              Upgrade
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Toaster position="bottom-center" />

      <UpgradeModal
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        mode={upgradeMode}
      />
    </div>
  );
}

// Dashboard wrapper that handles migration when user signs in
function DashboardWithMigration() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const [subStatus, setSubStatus] = useState({ checking: true, isActive: false });
  const [migrating, setMigrating] = useState(false);
  const [migrationDone, setMigrationDone] = useState(false);
  const migrationFiredRef = useRef(false);

  // Step 1: register token getter and fetch subscription status. We poll a
  // few times because a brand-new subscriber bouncing back from /success
  // may arrive before the Stripe webhook has updated our DB.
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    setTokenGetter(getToken);

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 6; // ~6s total
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await getMySubscription();
        if (cancelled) return;
        if (data?.is_active) {
          setSubStatus({ checking: false, isActive: true });
          return;
        }
        // Keep polling for a few seconds in case the webhook is just behind.
        // Only poll if we landed here from /success (URL hint); otherwise
        // accept the first answer.
        const params = new URLSearchParams(window.location.search);
        const cameFromCheckout = params.get('checkout') === 'success';
        if (cameFromCheckout && attempts < maxAttempts) {
          setTimeout(poll, 1000);
          return;
        }
        setSubStatus({ checking: false, isActive: false });
      } catch (err) {
        if (cancelled) return;
        // If the lookup itself fails, treat as no active subscription so
        // the user falls into guest mode rather than seeing a broken state.
        setSubStatus({ checking: false, isActive: false });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [isLoaded, isSignedIn, getToken]);

  // Step 2: once we know the user is subscribed, run guest-data migration.
  // We use a ref to ensure this fires exactly once per mount — depending on
  // `migrating` in the dep array previously caused the effect's cleanup to
  // run mid-flight, leaving the UI stuck on the spinner even after the
  // migration had successfully completed on the server.
  useEffect(() => {
    if (!subStatus.isActive) return;
    if (migrationFiredRef.current) return;
    migrationFiredRef.current = true;

    if (!hasGuestData()) {
      setMigrationDone(true);
      return;
    }

    let cancelled = false;
    setMigrating(true);
    (async () => {
      try {
        // Token getter is already registered by the previous effect.
        await new Promise((r) => setTimeout(r, 250));
        const guestData = getGuestDataForMigration();
        await migrateGuestData(guestData);
        clearGuestData();
        if (cancelled) return;
        toast.success(`Successfully migrated ${guestData.transactions.length} transactions to your account!`);
      } catch (err) {
        // Log the actual validation detail so 422s are debuggable in prod.
        // FastAPI puts field-level errors in err.response.data.detail.
        const detail = err?.response?.data?.detail;
        console.error('Migration failed:', err, detail ? { detail } : null);
        if (cancelled) return;
        toast.error('Failed to migrate guest data. Your data is still saved locally.');
      } finally {
        if (!cancelled) {
          setMigrating(false);
          setMigrationDone(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [subStatus.isActive]);

  if (subStatus.checking || migrating) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-muted-foreground">
            {migrating ? 'Migrating your data...' : 'Loading your account...'}
          </p>
        </div>
      </div>
    );
  }

  // Subscribed → full Dashboard. Unsubscribed → GuestDashboard so they see
  // the upgrade banner + Save Forecast button and keep using localStorage.
  return subStatus.isActive ? <Dashboard /> : <GuestDashboard />;
}

// Tiny invisible component that registers Clerk's getToken with the API
// instance. Mounted on every Clerk-aware page (landing, learn, success,
// /app) so an authenticated user can call the backend from any page —
// previously the registration only happened inside Dashboard, which broke
// e.g. the "Pricing" button on /learn.
function ClerkAuthBridge() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  useEffect(() => {
    if (isLoaded && isSignedIn) {
      setTokenGetter(getToken);
    }
  }, [isLoaded, isSignedIn, getToken]);
  return null;
}

function App({ devMode = false }) {
  // Initialize analytics on mount
  useEffect(() => {
    analytics.initAnalytics();
    analytics.trackSiteViewed();
    analytics.trackSessionStarted();
    
    // Track session end on page unload
    const handleUnload = () => {
      analytics.trackSessionEnded();
    };
    window.addEventListener('beforeunload', handleUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, []);

  // Landing page at root path
  const path = window.location.pathname;
  if (path === '/' || path === '') {
    return (
      <>
        {!devMode && <ClerkAuthBridge />}
        <LandingPage devMode={devMode} />
      </>
    );
  }
  // Learn More page
  if (path === '/learn' || path === '/learn-more') {
    return (
      <>
        {!devMode && <ClerkAuthBridge />}
        <LearnMorePage devMode={devMode} />
      </>
    );
  }
  // Stripe redirect targets
  if (path === '/success') {
    return (
      <>
        {!devMode && <ClerkAuthBridge />}
        <CheckoutSuccessPage />
      </>
    );
  }
  if (path === '/cancel') {
    return <CheckoutCancelPage />;
  }
  // Post-signup checkout resumer (Clerk redirects here after Sign Up).
  if (path === '/checkout-resume') {
    return <CheckoutResumePage devMode={devMode} />;
  }

  // In dev mode (no valid Clerk keys), show dashboard directly
  if (devMode) {
    return (
      <ThemeProvider defaultTheme="light" storageKey="cashforth-theme">
        <DevModeDashboard />
      </ThemeProvider>
    );
  }

  // Check for invite join flow
  const urlParams = new URLSearchParams(window.location.search);
  const inviteToken = urlParams.get('token');
  const isJoinPage = window.location.pathname === '/join';

  if (isJoinPage && inviteToken) {
    return (
      <ThemeProvider defaultTheme="light" storageKey="cashforth-theme">
        <JoinWorkspace 
          token={inviteToken} 
          onComplete={() => {
            window.history.pushState({}, '', '/app');
            window.location.reload();
          }}
        />
        <Toaster position="bottom-center" />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider defaultTheme="light" storageKey="cashforth-theme">
      <SignedIn>
        <DashboardWithMigration />
      </SignedIn>
      <SignedOut>
        <GuestDashboard />
      </SignedOut>
    </ThemeProvider>
  );
}

export default App;
